import React, { useCallback, useEffect, useMemo, useState } from "react";
import BaseModal from "../modals/BaseModal";
import { pipelineService } from "../../../../services/pipelineService";

const formatInr = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

function FieldCard({ label, value }) {
  return (
    <div className="flex-1 rounded-xl border border-gray-200 bg-[#F3F1EA] px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-gray-500">
        {label}
      </div>
      <div className="mt-0.5 text-xs sm:text-sm lg:text-lg font-semibold text-gray-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function getDeliverablesTextFromNegotiation(negotiation) {
  const d = negotiation?.deliverables || {};
  const reels = Number(d.reels ?? 0);
  const stories = Number(d.stories ?? 0);
  const posts = Number(d.posts ?? 0);
  const parts = [];
  if (reels) parts.push(`${reels} ${reels === 1 ? "Reel" : "Reels"}`);
  if (stories) parts.push(`${stories} ${stories === 1 ? "Story" : "Stories"}`);
  if (posts) parts.push(`${posts} ${posts === 1 ? "Post" : "Posts"}`);
  return parts.length ? parts.join(" + ") : "";
}

/** Most recent brand (company) row in the thread — used to prefill the reply form. */
function getLastCompanyOfferFromThread(thread) {
  if (!Array.isArray(thread) || thread.length === 0) return null;
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    const m = thread[i];
    if (m?.actor !== "company") continue;
    const amount = m.amountInr;
    const del = String(m.deliverablesText || "").trim();
    const hasAmount = amount != null && Number.isFinite(Number(amount));
    if (hasAmount || del) {
      return {
        proposedRateInr: hasAmount ? Number(amount) : "",
        deliverablesText: del,
      };
    }
  }
  return null;
}

function formatAmountInline(amountInr) {
  const n = Number(amountInr);
  if (!Number.isFinite(n)) return null;
  return `₹${n.toLocaleString("en-IN")}`;
}

function ThreadBubble({ who, tone, amountInr, text, align }) {
  const amt = amountInr != null ? formatAmountInline(amountInr) : null;
  const isRight = align === "right";
  return (
    <div className={`w-full flex ${isRight ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[min(92%,30rem)] flex flex-col gap-1.5 ${isRight ? "items-end" : "items-start"}`}
      >
        <div
          className={`text-[10px] sm:text-xs lg:text-sm font-medium text-gray-500 ${isRight ? "text-right" : "text-left"}`}
        >
          {who}
        </div>
        <div
          className={[
            "w-full rounded-xl border px-3 py-2 text-xs sm:text-sm lg:text-lg leading-relaxed",
            tone === "brand"
              ? "bg-[#FFE9DB] text-[#7A3B1C] border-orange-200"
              : "bg-white text-gray-800 border-gray-200",
          ].join(" ")}
        >
          {amt ? (
            <div className="font-semibold text-gray-900 mb-0.5">{amt}</div>
          ) : null}
          <div>{text}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {import('../../hooks/usePipelineBoard').PipelineCardModel | null} props.influencer
 * @param {number} [props.indicativeRateInr]
 * @param {() => void} props.onClose
 * @param {(text: string) => void} [props.onAddActivity]
 * @param {(nextCard: any) => void} [props.onUpdated]
 * @param {() => void} [props.onViewContract]
 */
export default function NegotiationModal({
  open,
  influencer,
  indicativeRateInr,
  onClose,
  onAddActivity,
  onUpdated,
  onViewContract,
}) {
  const itemId = influencer?.__pipelineItemId;
  const negotiation = influencer?.negotiation || null;
  const contract = influencer?.contract || null;
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [lastActor, setLastActor] = useState(null);

  const initial = useMemo(() => {
    return {
      proposedRateInr:
        negotiation?.proposedRateInr != null ? Number(negotiation.proposedRateInr) : "",
      deliverablesText:
        getDeliverablesTextFromNegotiation(negotiation) || "1 Reel + 3 Stories",
    };
  }, [negotiation]);

  const [saving, setSaving] = useState(false);
  const [proposedRateInr, setProposedRateInr] = useState(initial.proposedRateInr);
  const [deliverablesText, setDeliverablesText] = useState(initial.deliverablesText);

  const indicativeRate = Number(indicativeRateInr || 0);
  const agreedFigure =
    negotiation?.status === "agreed" && negotiation?.proposedRateInr != null
      ? Number(negotiation.proposedRateInr)
      : null;
  const agreedRateLabel =
    agreedFigure != null && Number.isFinite(agreedFigure) ? formatInr(agreedFigure) : "Pending";
  const isAgreed = negotiation?.status === "agreed";
  const inNegotiating = influencer?.stageId === "negotiating";
  const hasContract =
    Boolean(contract?.status) && String(contract.status) !== "none";
  const showSendProposal = inNegotiating && !isAgreed;
  const showGenerateContract = isAgreed && !hasContract;
  const showViewContract = hasContract;

  const loadThread = useCallback(async () => {
    if (!itemId) return;
    setThreadLoading(true);
    try {
      const data = await pipelineService.getNegotiationThread({ itemId });
      setThread(Array.isArray(data?.thread) ? data.thread : []);
      setLastActor(data?.lastActor ?? null);
    } finally {
      setThreadLoading(false);
    }
  }, [itemId]);

  const onGenerateContract = useCallback(async () => {
    if (!itemId) return;
    setSaving(true);
    try {
      const updated = await pipelineService.generateContract({ itemId });
      onAddActivity?.("Contract: generated");
      onUpdated?.(updated);
    } finally {
      setSaving(false);
    }
  }, [itemId, onAddActivity, onUpdated]);

  const onSave = useCallback(async () => {
    if (!itemId) return;
    setSaving(true);
    try {
      const updated = await pipelineService.updateNegotiation({
        itemId,
        status: "under_discussion",
        platform: "Instagram",
        proposedRateInr: proposedRateInr === "" ? 0 : Number(proposedRateInr),
      });

      const proposalText =
        message.trim() ||
        `Hi ${String(influencer?.name || "there").split(/\s+/)[0]}! We proposed ₹${Number(
          proposedRateInr || 0
        ).toLocaleString("en-IN")} for ${deliverablesText}. Please review.`;

      const msgRes = await pipelineService.postNegotiationMessage({
        itemId,
        text: proposalText,
        amountInr: proposedRateInr === "" ? null : Number(proposedRateInr),
        deliverablesText,
      });
      setThread(Array.isArray(msgRes?.thread) ? msgRes.thread : []);
      setMessage("");
      onAddActivity?.("Negotiation: proposal updated");
      onUpdated?.(updated);
    } finally {
      setSaving(false);
    }
  }, [deliverablesText, influencer?.name, itemId, message, onAddActivity, onUpdated, proposedRateInr]);

  useEffect(() => {
    if (!open) return;
    setThread([]);
    loadThread();
  }, [open, loadThread]);

  // When opening or switching pipeline item, seed from negotiation until thread loads.
  useEffect(() => {
    if (!open || !itemId) return;
    setProposedRateInr(initial.proposedRateInr);
    setDeliverablesText(initial.deliverablesText);
  }, [open, itemId, initial.proposedRateInr, initial.deliverablesText]);

  // After thread loads, prefill inputs with the last offer the brand sent (matches thread bubbles).
  useEffect(() => {
    if (!open || !itemId || threadLoading) return;
    const fromThread = getLastCompanyOfferFromThread(thread);
    if (!fromThread) return;
    if (fromThread.proposedRateInr !== "" && Number.isFinite(Number(fromThread.proposedRateInr))) {
      setProposedRateInr(fromThread.proposedRateInr);
    }
    setDeliverablesText(
      fromThread.deliverablesText || initial.deliverablesText
    );
  }, [open, itemId, threadLoading, thread, initial.deliverablesText]);

  return (
    <BaseModal
      open={open}
      title={`Negotiate — ${influencer?.name ?? "Influencer"}`}
      onClose={onClose}
      className="max-w-[920px]"
    >
      <div className="p-4 sm:p-5 bg-white">
        <div className="rounded-2xl border border-gray-200 bg-[#F3F1EA] px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0">
              <span className="text-xs sm:text-sm lg:text-lg font-semibold text-[#1A1714]">
                {String(influencer?.name || "I")
                  .trim()
                  .split(/\s+/)
                  .slice(0, 2)
                  .map((p) => p[0]?.toUpperCase())
                  .join("")}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm lg:text-lg font-semibold text-gray-900 truncate">
                {influencer?.name}
              </div>
              <div className="text-xs sm:text-sm lg:text-lg text-gray-500 truncate">
                {influencer?.handle} · Summer Glow 2025
              </div>
            </div>
          </div>

          {isAgreed ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs sm:text-sm lg:text-lg font-semibold text-emerald-700 shrink-0">
              ✓ Rate agreed:{" "}
              <span className="font-semibold text-emerald-800">
                {formatInr(agreedFigure ?? 0)}
              </span>
            </div>
          ) : null}
        </div>

        {inNegotiating && lastActor ? (
          <div className="mt-2 rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs sm:text-sm text-amber-950 font-medium">
            {lastActor === "company"
              ? "Waiting for influencer"
              : lastActor === "influencer"
                ? "Your turn"
                : null}
          </div>
        ) : null}

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldCard label="Indicative rate" value={formatInr(indicativeRate)} />
          <FieldCard label="Agreed rate" value={agreedRateLabel} />
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldCard label="Deliverables" value={deliverablesText || "—"} />
          <FieldCard label="Platform" value="Instagram" />
        </div>

        <div className="mt-4">
          <div className="text-[10px] sm:text-xs lg:text-sm font-medium tracking-[0.08em] text-gray-500 uppercase">
            Negotiation thread
          </div>

          <div className="mt-2 rounded-2xl border border-gray-200 bg-[#F3F1EA] p-3">
            {threadLoading ? (
              <div className="text-xs sm:text-sm lg:text-lg text-gray-500">Loading messages…</div>
            ) : thread.length === 0 ? (
              <div className="text-xs sm:text-sm lg:text-lg text-gray-500 text-center">
                No messages yet — propose a rate to begin
              </div>
            ) : (
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                {thread.map((m, idx) => {
                  const isCompany = m.actor === "company";
                  return (
                    <ThreadBubble
                      key={`${m.at ? new Date(m.at).toISOString() : "m"}-${idx}`}
                      who={isCompany ? "You (Brand)" : "Influencer"}
                      tone={isCompany ? "brand" : "influencer"}
                      amountInr={m.amountInr}
                      text={String(m.text || "").trim()}
                      align={isCompany ? "right" : "left"}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          {showSendProposal ? (
            <>
              <div>
                <div className="text-xs sm:text-sm lg:text-lg font-medium text-gray-700 mb-1.5">
                  Your offer (₹)
                </div>
                <input
                  type="number"
                  min={0}
                  value={proposedRateInr}
                  onChange={(e) => setProposedRateInr(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm lg:text-lg font-medium text-gray-900 tabular-nums outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300"
                  placeholder="85000"
                />
              </div>
              <div>
                <div className="text-xs sm:text-sm lg:text-lg font-medium text-gray-700 mb-1.5">
                  Deliverables
                </div>
                <input
                  type="text"
                  value={deliverablesText}
                  onChange={(e) => setDeliverablesText(e.target.value)}
                  className="w-full rounded-xl border-2 border-orange-500 bg-white px-3 py-2 text-xs sm:text-sm lg:text-lg font-medium text-gray-900 outline-none focus:ring-2 focus:ring-orange-200"
                  placeholder="1 Reel + 3 Stories"
                />
              </div>
              <button
                type="button"
                disabled={!itemId || saving}
                onClick={onSave}
                className={[
                  "h-[42px] sm:h-[44px] lg:h-[52px] px-5 sm:px-6 rounded-xl text-xs sm:text-sm lg:text-lg font-semibold transition-colors",
                  saving || !itemId
                    ? "bg-orange-300 text-white cursor-not-allowed"
                    : "bg-orange-500 text-white hover:bg-orange-600",
                ].join(" ")}
              >
                {saving ? "Sending…" : "Send proposal"}
              </button>
            </>
          ) : null}

          {showGenerateContract || showViewContract ? (
            <div className="sm:col-span-3 flex flex-wrap items-center justify-end gap-2">
              {showGenerateContract ? (
                <button
                  type="button"
                  disabled={!itemId || saving}
                  onClick={onGenerateContract}
                  className={[
                    "h-[42px] sm:h-[44px] lg:h-[52px] px-5 sm:px-6 rounded-xl text-xs sm:text-sm lg:text-lg font-semibold transition-colors",
                    saving || !itemId
                      ? "bg-orange-300 text-white cursor-not-allowed"
                      : "bg-orange-500 text-white hover:bg-orange-600",
                  ].join(" ")}
                >
                  {saving ? "Generating…" : "Generate contract →"}
                </button>
              ) : null}
              {showViewContract ? (
                <button
                  type="button"
                  onClick={() => onViewContract?.()}
                  className="h-[42px] sm:h-[44px] lg:h-[52px] px-5 sm:px-6 rounded-xl text-xs sm:text-sm lg:text-lg font-semibold border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                >
                  View contract
                </button>
              ) : null}
            </div>
          ) : null}

          {!showSendProposal && !showGenerateContract && !showViewContract ? (
            <p className="sm:col-span-3 text-xs sm:text-sm text-gray-500">
              {inNegotiating
                ? "Negotiation is complete for this stage."
                : "Open this card from the Negotiating column to send proposals or generate a contract."}
            </p>
          ) : null}
        </div>
      </div>
    </BaseModal>
  );
}

